import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, toIsoDate } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, DatePicker, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const REGISTER_TAB = '/assets?tab=register';
const DEFAULT_INTERVAL_DAYS = 180;
const DEFAULT_LEAD_DAYS = 30;

interface AssetSummary {
  id: string;
  code: string;
  name: string;
}

export function SchedulePage() {
  const [params] = useSearchParams();
  const assetId = params.get('assetId') ?? '';
  const returnTo = safeReturnTo(params.get('returnTo'), REGISTER_TAB);

  const asset = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => get<AssetSummary>(`/assets/${assetId}`),
    enabled: Boolean(assetId),
  });

  if (!assetId) return <RecordState missing />;
  if (!asset.data) return <RecordState error={asset.error} missing={asset.isSuccess} />;
  return <ScheduleForm key={asset.data.id} asset={asset.data} returnTo={returnTo} />;
}

function ScheduleForm({ asset, returnTo }: { asset: AssetSummary; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initialDue] = useState(() => toIsoDate(new Date(Date.now() + DEFAULT_LEAD_DAYS * 86_400_000)));
  const [kind, setKind] = useState('preventive');
  const [intervalDays, setIntervalDays] = useState(DEFAULT_INTERVAL_DAYS);
  const [nextDueAt, setNextDueAt] = useState(initialDue);
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      post('/assets/schedules', {
        assetId: asset.id,
        kind,
        intervalDays,
        nextDueAt: new Date(`${nextDueAt}T00:00:00.000Z`).toISOString(),
        instructions: instructions || undefined,
      }),
    onSuccess: () => {
      toast.success('Schedule set', 'A ticket is raised on its own when it falls due');
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['maintenance-due'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !save.isSuccess && (kind !== 'preventive' || intervalDays !== DEFAULT_INTERVAL_DAYS || nextDueAt !== initialDue || instructions !== '');

  return (
    <FormScreen
      title={`Maintenance schedule — ${asset.name}`}
      description="The nightly job turns a due schedule into a ticket"
      eyebrow={asset.code}
      icon={CalendarClock}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('admin', 'service')} icon={CalendarClock} columns={3}>
        <Field label="Kind">
          <ChoiceButtons
            value={kind}
            onChange={setKind}
            options={['preventive', 'calibration', 'inspection'].map((k) => ({ value: k, label: humanize(k) }))}
          />
        </Field>
        <Field label="Every (days)">
          <Input type="number" min={1} value={intervalDays} onChange={(e) => setIntervalDays(Number(e.target.value))} />
        </Field>
        <Field label="Next due">
          <DatePicker value={nextDueAt} onChange={setNextDueAt} />
        </Field>
        <Field label="Instructions" className="sm:col-span-2 lg:col-span-3">
          <Textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Save" loading={save.isPending} cancelTo={returnTo} />
    </FormScreen>
  );
}
